import React, { useCallback, useEffect, useRef, useState } from "react";
import { MeasurementSystem } from "velavu-js-api";
import { useTranslation } from "react-i18next";
import VelavuAvatar from "../../elements/velavu-avatar";
import VelavuButton from "../../elements/velavu-button";
import VelavuInput from "../../elements/velavu-input";
import VelavuSelect from "../../elements/velavu-select";
import { getBase64FromUrl } from "../../helper/image-helper";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import ComponentSaveBar from "./component-save-bar";
import * as styles from "./settings-user.module.scss";
import { VelavuUser } from "velavu-js-api";
import VelavuSwitchBlock from "../../elements/velavu-switch-block";

type LanguageSelection = "system" | "en" | "de";

function toLanguageSelection(language: string | null): LanguageSelection {
	return language === "en" || language === "de" ? language : "system";
}

export default function SettingsUser(props: {
	changePassword: VoidFunction;
	isDirty: boolean;
	setDirty: (dirty: boolean) => void;
}) {
	const { t } = useTranslation();
	const userContext = useSignInContext();
	const userAttrName = userContext.velavuUser?.name;
	const userAttrEmail = userContext.velavuUser?.email;
	const userProfileImage = userContext.velavuUser?.profile_img_url;

	const userPrefUnits = userContext.velavuUser?.settings?.measurementSystem;

	const resolvedPreferences = userContext.preferences;

	const [username, setUsername] = useState<string | undefined>(undefined);
	const [units, setUnits] = useState<MeasurementSystem>(
		resolvedPreferences.measurementSystem,
	);
	const [language, setLanguage] = useState<string | null>(
		resolvedPreferences.language,
	);
	const [rulesEngineVisible, setRulesEngineVisible] = useState<boolean>(
		resolvedPreferences.showRulesEngine ?? false,
	);
	const [image, setImage] = useState<string | undefined>(undefined);
	const imageInput = useRef<HTMLInputElement>(null);

	const [originalUsername, setOriginalUsername] = useState<string>("");
	const [originalUnits, setOriginalUnits] = useState<MeasurementSystem>(
		resolvedPreferences.measurementSystem,
	);
	const [originalLanguage, setOriginalLanguage] = useState<string | null>(
		resolvedPreferences.language,
	);
	const [originalRulesEngineVisible, setOriginalRulesEngineVisible] =
		useState<boolean>(resolvedPreferences.showRulesEngine ?? false);

	const updateUser = userContext.updateVelavuUser;
	const updateProfileImage = userContext.updateVelavuUserImage;

	const [isLoading, setLoading] = useState(false);
	const [lastSaveResult, setLastSaveResult] = useState<boolean | null>(null);

	const onChangeImage = useCallback(
		(event: React.FormEvent<HTMLInputElement>) => {
			//TODO: This creates a memory leak. Call revokeObjectURL() to release.
			const imageURL = URL.createObjectURL(event.currentTarget.files![0]);
			getBase64FromUrl(imageURL!).then(updateProfileImage);
		},
		[updateProfileImage],
	);

	const handleLanguageChange = useCallback(
		(value: LanguageSelection) => {
			setLanguage(value === "system" ? null : value);
		},
		[setLanguage],
	);

	const saveChanges = useCallback(async () => {
		//Create the updated user
		const updatedUser: Partial<VelavuUser> & Pick<VelavuUser, "settings"> =
			{
				settings: {},
			};

		const trimmedUsername = username!.trim();
		if (trimmedUsername !== originalUsername) {
			updatedUser.name = trimmedUsername;
		}

		if (units !== originalUnits) {
			updatedUser.settings.measurementSystem = units;
		}

		if (language !== originalLanguage) {
			updatedUser.settings.language = language;
		}

		if (rulesEngineVisible !== originalRulesEngineVisible) {
			updatedUser.settings.showRulesEngine = rulesEngineVisible;
		}

		//Create the requests
		const promiseArray: Promise<unknown>[] = [];
		if (Object.keys(updatedUser).length > 0) {
			promiseArray.push(updateUser(updatedUser));
		}

		//Wait for all promises to resolve
		Promise.all(promiseArray)
			.then(() => {
				setLastSaveResult(true);
			})
			.catch((error) => {
				console.log(error);
				setLastSaveResult(false);
			})
			.finally(() => setLoading(false));
	}, [
		username,
		originalUsername,
		units,
		originalUnits,
		language,
		originalLanguage,
		rulesEngineVisible,
		originalRulesEngineVisible,
		updateUser,
	]);

	//Update the name if the attribute changes
	useEffect(() => {
		if (!userAttrName) return;

		setUsername(userAttrName);
		setOriginalUsername(userAttrName);
	}, [setUsername, setOriginalUsername, userAttrName]);

	//Update the profile image if it changes within the sign-in context
	useEffect(() => {
		setImage(userProfileImage);
	}, [setImage, userProfileImage]);

	//Update the measurement system if the attribute changes
	useEffect(() => {
		if (!userPrefUnits) return;

		setUnits(userPrefUnits);
		setOriginalUnits(userPrefUnits);
	}, [setUnits, setOriginalUnits, userPrefUnits]);

	//Update the language if the attribute changes
	useEffect(() => {
		setLanguage(resolvedPreferences.language);
		setOriginalLanguage(resolvedPreferences.language);
	}, [setLanguage, setOriginalLanguage, resolvedPreferences.language]);

	//Update the rules engine visibility if the attribute changes
	useEffect(() => {
		const value = resolvedPreferences.showRulesEngine ?? false;
		setRulesEngineVisible(value);
		setOriginalRulesEngineVisible(value);
	}, [
		setRulesEngineVisible,
		setOriginalRulesEngineVisible,
		resolvedPreferences.showRulesEngine,
	]);

	//Update the dirty state
	const propsSetDirty = props.setDirty;
	useEffect(() => {
		propsSetDirty(
			(username !== undefined && username.trim() !== originalUsername) ||
				rulesEngineVisible !== originalRulesEngineVisible ||
				units !== originalUnits ||
				language !== originalLanguage,
		);
	}, [
		username,
		originalUsername,
		units,
		originalUnits,
		language,
		originalLanguage,
		rulesEngineVisible,
		originalRulesEngineVisible,
		propsSetDirty,
	]);

	return (
		<div className={styles.container}>
			<div className={styles.profileHeader}>
				<VelavuAvatar
					className={styles.profileAvatar}
					name={userAttrName ?? ""}
					image={image}
				/>
				<button
					className={styles.imageButton}
					onClick={() => imageInput.current?.click()}
				>
					{image
						? t("settings.user.upload_new_photo")
						: t("settings.user.upload_photo")}
				</button>
				{image && (
					<div>
						<span className={styles.bulletPoint} />
						<button
							className={styles.imageButton}
							onClick={() => updateProfileImage(undefined)}
						>
							{t("settings.user.remove_photo")}
						</button>
					</div>
				)}
				<input
					className={styles.inputImage}
					ref={imageInput}
					onChange={onChangeImage}
					type="file"
					name="image"
					accept="image/png, image/jpeg"
				/>
			</div>

			<div className={styles.inputSection}>
				<div className={styles.inputColumn}>
					<VelavuInput
						value={username ?? ""}
						onChange={setUsername}
						titleText={t("settings.user.name")}
						disabled={username === undefined}
						fullWidth
						bottomPadding
					/>
					<VelavuSelect
						value={units ?? "imperial"}
						onChange={setUnits}
						titleText={t("settings.user.display_unit")}
						fullWidth
						disabled={units === undefined}
						bottomPadding
					>
						<option value="imperial">
							{t("settings.user.imperial")}
						</option>
						<option value="metric">
							{t("settings.user.metric")}
						</option>
					</VelavuSelect>
					<VelavuSelect
						value={toLanguageSelection(language)}
						onChange={handleLanguageChange}
						titleText={t("settings.user.language")}
						fullWidth
						bottomPadding
					>
						<option value="system">
							{t("settings.user.language_system_default")}
						</option>
						<option value="en">English</option>
						<option value="de">Deutsch</option>
					</VelavuSelect>

					<VelavuSwitchBlock
						toggled={rulesEngineVisible}
						onChange={setRulesEngineVisible}
						label={t("settings.user.rules_engine_visibility")}
					/>
				</div>
				<div className={styles.inputSpacer} />
				<div className={styles.inputColumn}>
					<VelavuInput
						value={userAttrEmail ?? ""}
						titleText={t("settings.user.email")}
						fullWidth
						disabled
						bottomPadding
						noAutofill
					/>
					<VelavuInput
						value={"----------"}
						titleText={t("settings.user.password")}
						type="password"
						autoComplete="new-password"
						fullWidth
						disabled
						bottomPadding
						noAutofill
					/>
					<VelavuButton
						onClick={props.changePassword}
						label={t("settings.user.change_password")}
						size="small"
						variant="outlined"
					/>
				</div>
			</div>

			<ComponentSaveBar
				className={styles.saveBar}
				disabled={!props.isDirty || isLoading}
				onClick={saveChanges}
				labelOK={t("settings.user.saved")}
				labelError={t("settings.user.save_failed")}
				result={lastSaveResult}
			/>
		</div>
	);
}
